// Reads KYC fields out of the OCR text of an ID card image (ocrService.extractText).
// Shared by the staff onboarding modal and the investor profile form, so both fill
// the same fields the same way. Best effort: anything not found is left out.

export type KycDocType = 'aadhaar_front' | 'aadhaar_back' | 'pan_card';

export interface KycOcrFields {
    aadhar_number?: string;
    dob?: string;          // YYYY-MM-DD
    gender?: 'Male' | 'Female' | 'Other';
    pincode?: string;
    aadhar_address?: string;
    pan_number?: string;
}

// Human labels for the snackbar ("Filled from document: …").
export const KYC_OCR_LABELS: Record<keyof KycOcrFields, string> = {
    aadhar_number: 'Aadhaar Number', dob: 'Date of Birth', gender: 'Gender',
    pincode: 'Pincode', aadhar_address: 'Aadhaar Address', pan_number: 'PAN Number',
};

export const parseOcrDate = (dateStr: string): string | null => {
    const cleanStr = dateStr.replace(/\s+/g, '');
    const m = cleanStr.match(/(\d{1,2})[/-](\d{1,2})[/-](\d{4})/);
    if (m) {
        const dayNum = parseInt(m[1], 10);
        const monthNum = parseInt(m[2], 10);
        // OCR misreads a digit often enough (scanned ID photos) that an
        // out-of-range day/month must be rejected here, before it ever reaches
        // a date input — otherwise it can't render it and the NaN it produces
        // later slips straight past the age check.
        if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) return null;
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
    return null;
};

const findDob = (text: string): string | undefined => {
    // Allow spaces inside the slash/hyphen separations e.g. 15 / 08 / 1987
    const m = text.match(/(?:DOB|Date\s*of\s*Birth|Birth\s*Date|D\.O\.B)[\s:.-]{1,5}(\d{2}\s*[/-]\s*\d{2}\s*[/-]\s*\d{4})/i) ||
              text.match(/\b(\d{2}\s*[/-]\s*\d{2}\s*[/-]\s*\d{4})\b/);
    return (m && parseOcrDate(m[1])) || undefined;
};

// OCR often reads a digit as a look-alike letter (2O75, 9O66, 1l23). In 4-character groups
// that are mostly digits, map those letters back so the Aadhaar number still matches.
const LOOKALIKE: Record<string, string> = { O: '0', o: '0', D: '0', Q: '0', I: '1', l: '1', '|': '1', S: '5', B: '8', Z: '2' };
const digitGroups = (text: string) => text.replace(/(?<![A-Za-z0-9|])[0-9OoDQIl|SBZ]{4}(?![A-Za-z0-9|])/g, g =>
    (g.match(/\d/g) || []).length >= 2 ? g.replace(/[OoDQIl|SBZ]/g, c => LOOKALIKE[c]) : g);

export const parseKycOcr = (text: string, type: KycDocType): KycOcrFields => {
    const out: KycOcrFields = {};

    if (type === 'aadhaar_front') {
        // Aadhaar number: 4-4-4 digits on one line (OCR's letter-for-digit slips undone). The card
        // also prints a 16-digit VID; that's taken out first so its digits are never used instead.
        const num = digitGroups(text);
        const noVid = num.replace(/\b\d{4}[ \t-]*\d{4}[ \t-]*\d{4}[ \t-]*\d{4}\b/g, ' ');
        const candidates = Array.from(new Set(
            (noVid.match(/\b\d{4}[ \t-]*\d{4}[ \t-]*\d{4}\b/g) || []).map(m => m.replace(/[ \t-]/g, ''))));
        const aadhaar = candidates[0];
        if (aadhaar) out.aadhar_number = aadhaar;

        const dob = findDob(text);
        if (dob) out.dob = dob;

        // Gender: "Male", "Female", or short form "M" / "F" after a Gender/Sex label
        const g = (text.match(/(?:Gender|Sex)[\s:.-]{1,5}\b(Male|Female|M|F)\b/i) || text.match(/\b(Male|Female)\b/i))?.[1].toLowerCase();
        if (g) out.gender = g.startsWith('m') ? 'Male' : g.startsWith('f') ? 'Female' : 'Other';
    }

    if (type === 'aadhaar_back') {
        const pin = text.match(/\b([1-9]\d{5})\b/);
        if (pin) out.pincode = pin[1];

        // Aadhaar address: everything after an "Address" label, stopping before the pincode
        // (captured separately above). This is exactly what's printed on the card, so it
        // fills the Aadhaar address — not the permanent/current address, which may differ.
        const addr = text.match(/Address[\s:.-]{1,5}([\s\S]{10,300}?)(?=\b[1-9]\d{5}\b|$)/i);
        if (addr) {
            const cleaned = addr[1]
                .replace(/[\r\n]+/g, ', ')
                .replace(/\s{2,}/g, ' ')
                .replace(/,\s*,+/g, ',')
                .replace(/^[,\s]+|[,\s]+$/g, '')
                .trim();
            if (cleaned) out.aadhar_address = cleaned;
        }
    }

    if (type === 'pan_card') {
        // Only the PAN number: date of birth comes from the Aadhaar front.
        const pan = text.match(/\b([A-Z]{5}\d{4}[A-Z])\b/i);
        if (pan) out.pan_number = pan[1].toUpperCase();
    }

    return out;
};
